import {
  DropDown,
  FormControls,
  Radio,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { SETTINGS } from '@elementary/common-ui/constants/queryKeys';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { FormControlLabel, RadioGroup } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Control, Controller, UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';
import { AddTestForm, FetchAccountSettingsApi } from 'shared/types';
import { DAYS, HOURS } from 'shared/utils/cronUtils';
import {
  CUSTOM_SQL_INTERVAL_HOURS,
  CUSTOM_SQL_MINUTES,
  DEFAULT_CLOUD_SQL_TEST_SCHEDULE,
} from './constants';
import {
  getCustomCronNextRun,
  isCloudSqlTestScheduleType,
} from './customSqlCloudTest';

const HOUR_OPTIONS = HOURS.map((h) => ({
  value: String(h.value),
  label: h.label.replace(':00', ''),
}));

const MINUTE_OPTIONS = CUSTOM_SQL_MINUTES.map((minute) => ({
  value: String(minute),
  label: minute.toString().padStart(2, '0'),
}));

const INTERVAL_OPTIONS = CUSTOM_SQL_INTERVAL_HOURS.map((hours) => ({
  value: String(hours),
  label: pluralizeString(hours, 'hour', 'hours'),
}));

const DAY_OPTIONS = DAYS.map((d) => ({
  value: String(d.value),
  label: d.label,
}));

const HOUR_DROPDOWN_MENU_WIDTH = '120px';
const MINUTE_DROPDOWN_MENU_WIDTH = '96px';

type ControlProps = {
  control: Control<AddTestForm>;
};

const IntervalHoursDropDown = ({ control }: ControlProps) => (
  <Controller
    control={control}
    name="intervalHours"
    shouldUnregister={false}
    rules={{ required: requiredLabel }}
    render={({ field }) => (
      <DropDown
        size="large"
        value={String(field.value ?? 6)}
        onChange={(val) => field.onChange(Number(val))}
        options={INTERVAL_OPTIONS}
        showClear={false}
        sortOptions={false}
      />
    )}
  />
);

const DayOfWeekDropDown = ({ control }: ControlProps) => (
  <Controller
    control={control}
    name="scheduleDayOfWeek"
    shouldUnregister={false}
    rules={{ required: requiredLabel }}
    render={({ field }) => (
      <DropDown
        size="large"
        value={String(field.value ?? 1)}
        onChange={(val) => field.onChange(Number(val))}
        options={DAY_OPTIONS}
        showClear={false}
        sortOptions={false}
      />
    )}
  />
);

const HourDropDown = ({ control }: ControlProps) => (
  <Controller
    control={control}
    name="scheduleHour"
    shouldUnregister={false}
    rules={{ required: requiredLabel }}
    render={({ field }) => (
      <DropDown
        size="large"
        sx={{ minWidth: 96 }}
        menuWidth={HOUR_DROPDOWN_MENU_WIDTH}
        value={String(field.value ?? 9)}
        onChange={(val) => field.onChange(Number(val))}
        options={HOUR_OPTIONS}
        showClear={false}
        sortOptions={false}
      />
    )}
  />
);

const MinuteDropDown = ({ control }: ControlProps) => (
  <Controller
    control={control}
    name="scheduleMinute"
    shouldUnregister={false}
    rules={{ required: requiredLabel }}
    render={({ field }) => (
      <DropDown
        size="large"
        sx={{ minWidth: 80 }}
        menuWidth={MINUTE_DROPDOWN_MENU_WIDTH}
        value={String(field.value ?? 0)}
        onChange={(val) => field.onChange(Number(val))}
        options={MINUTE_OPTIONS}
        showClear={false}
        sortOptions={false}
      />
    )}
  />
);

const CronTextField = styled(TextField)`
  width: 180px;

  .MuiFormHelperText-root {
    position: absolute;
    top: 100%;
    margin: 4px 0 0;
    font-size: 12px;
    white-space: nowrap;
  }
`;

type CustomCronFieldProps = {
  methods: UseFormReturn<AddTestForm>;
};

const CustomCronField = ({ methods }: CustomCronFieldProps) => {
  const { data: accountSettings } = useQuery<FetchAccountSettingsApi>(
    [SETTINGS],
    { enabled: false },
  );
  const cron = methods.watch('scheduleCron');
  const nextRun = getCustomCronNextRun(
    cron,
    accountSettings?.timezone ?? undefined,
  );

  return (
    <>
      <Controller
        control={methods.control}
        name="scheduleCron"
        shouldUnregister={false}
        render={({ field, fieldState }) => (
          <CronTextField
            {...field}
            size="large"
            placeholder="0 9 * * 1"
            value={field.value ?? ''}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            onChange={(event) => {
              methods.clearErrors('scheduleCron');
              field.onChange(event.target.value);
            }}
            inputProps={{ 'aria-label': 'Custom cron' }}
          />
        )}
      />
      {nextRun && (
        <FormControls.FormText>
          Next run: {localizeDate(nextRun).format('ddd, MMM D, h:mm A')}
        </FormControls.FormText>
      )}
    </>
  );
};

type Props = {
  methods: UseFormReturn<AddTestForm>;
};

const CloudTestScheduleFields = ({ methods }: Props) => {
  const scheduleType =
    methods.watch('scheduleType') ??
    DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleType;

  return (
    <>
      <FormControls.FormTitle>Test cadence</FormControls.FormTitle>
      <FormControls.NestedFields>
        <Controller
          control={methods.control}
          name="scheduleType"
          shouldUnregister={false}
          rules={{ required: requiredLabel }}
          render={({ field }) => (
            <RadioGroup
              name={field.name}
              value={
                field.value ?? DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleType
              }
              sx={{ gap: 1 }}
              onBlur={field.onBlur}
              ref={field.ref}
              onChange={(_, value) => {
                if (value == null) return;
                if (!isCloudSqlTestScheduleType(value)) return;
                if (value === 'every_x_hours') {
                  if (methods.getValues('intervalHours') == null) {
                    methods.setValue(
                      'intervalHours',
                      DEFAULT_CLOUD_SQL_TEST_SCHEDULE.intervalHours,
                    );
                  }
                } else {
                  if (methods.getValues('scheduleHour') == null) {
                    methods.setValue(
                      'scheduleHour',
                      DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleHour,
                    );
                  }
                  if (methods.getValues('scheduleMinute') == null) {
                    methods.setValue(
                      'scheduleMinute',
                      DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleMinute,
                    );
                  }
                  if (
                    value === 'weekly' &&
                    methods.getValues('scheduleDayOfWeek') == null
                  ) {
                    methods.setValue(
                      'scheduleDayOfWeek',
                      DEFAULT_CLOUD_SQL_TEST_SCHEDULE.scheduleDayOfWeek,
                    );
                  }
                }
                field.onChange(value);
              }}
            >
              <FormControlLabel
                sx={{ gap: 1 }}
                value="every_x_hours"
                control={<Radio size="small" />}
                label={
                  <FormControls.Row>
                    <FormControls.FormText>Every</FormControls.FormText>
                    {scheduleType === 'every_x_hours' && (
                      <IntervalHoursDropDown control={methods.control} />
                    )}
                  </FormControls.Row>
                }
              />
              <FormControlLabel
                sx={{ gap: 1 }}
                value="daily"
                control={<Radio size="small" />}
                label={
                  <FormControls.Row>
                    <FormControls.FormText>Daily</FormControls.FormText>
                    {scheduleType === 'daily' && (
                      <>
                        <HourDropDown control={methods.control} />
                        <MinuteDropDown control={methods.control} />
                      </>
                    )}
                  </FormControls.Row>
                }
              />
              <FormControlLabel
                sx={{ gap: 1 }}
                value="weekly"
                control={<Radio size="small" />}
                label={
                  <FormControls.Row>
                    <FormControls.FormText>Weekly</FormControls.FormText>
                    {scheduleType === 'weekly' && (
                      <>
                        <DayOfWeekDropDown control={methods.control} />
                        <HourDropDown control={methods.control} />
                        <MinuteDropDown control={methods.control} />
                      </>
                    )}
                  </FormControls.Row>
                }
              />
              <FormControlLabel
                sx={{ gap: 1 }}
                value="custom"
                control={<Radio size="small" />}
                label={
                  <FormControls.Row>
                    <FormControls.FormText>Custom</FormControls.FormText>
                    {scheduleType === 'custom' && (
                      <CustomCronField methods={methods} />
                    )}
                  </FormControls.Row>
                }
              />
            </RadioGroup>
          )}
        />
      </FormControls.NestedFields>
    </>
  );
};

export default CloudTestScheduleFields;
